import type { CSSProperties, ReactNode } from 'react';
import { Check, ChevronDown, Loader2, Search } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDebouncedValue } from '@/shared/lib';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import css from './server-search-select.module.scss';

// Vertical gap between the trigger and the portaled dropdown, mirroring Select.
const PORTAL_DROPDOWN_GAP = 4;
const PORTAL_DROPDOWN_MAX_HEIGHT = 320;
// Keep a portaled menu this far clear of the viewport's right edge.
const PORTAL_DROPDOWN_EDGE_MARGIN = 8;
// Min width for the menu under a content-sized `button` trigger.
const BUTTON_MENU_MIN_WIDTH = 260;

type PortalPosition = {
  top: number;
  left: number;
  right: number;
  width: number;
  placement: 'top' | 'bottom';
  // True when a left-anchored menu would spill past the viewport's right edge,
  // so it should hang from the trigger's right instead (mirrors `Select`).
  alignRight: boolean;
};

const styles = pickCss(css, css.root);

type Size = 'sm' | 'default' | 'lg';

// Generic search hook contract the consumer passes in. Mirrors the
// TanStack-Query useQuery shape: `{ data?, isLoading }`.
export type ServerSearchHookResult<T> = {
  data?: T[];
  isLoading: boolean;
  // Optional, so existing consumers keep working unchanged. Pass the query's
  // `isError` through and a FAILED lookup says so instead of rendering the
  // empty message: a request that errors returns no rows, which is otherwise
  // indistinguishable from "the catalog has no match" and sends the consultant
  // hunting for missing data that is actually there. That mistranslation is
  // what a reversed item time window did to the operator picker on 2026-08-11
  // ("None of the names on this Guide appear to be added").
  isError?: boolean;
};

type ServerSearchSelectProps<T> = {
  value: string | null;
  // The label to show in the closed-state trigger when `value` is set.
  // Pre-resolved by the caller; the search hook only fires when the
  // panel is open so the picker doesn't double as a lookup endpoint.
  selectedLabel?: string | null;
  onChange: (id: string | null) => void;
  // Receives the live query and whether the panel is open. Consumers should
  // gate their request's `enabled` on the `open` flag so a closed picker never
  // fetches — otherwise every embedded picker fires a request on first render.
  useSearch: (query: string, open: boolean) => ServerSearchHookResult<T>;
  getOptionValue: (item: T) => string;
  getOptionLabel: (item: T) => string;
  // Optional rich row content rendered in the dropdown in place of the plain
  // label (the closed-state trigger still uses `getOptionLabel`/`selectedLabel`).
  getOptionContent?: (item: T) => ReactNode;
  // Optional trailing decoration rendered at the right of each row —
  // used by the Customer picker to surface a warning icon when an
  // expected field (e.g. email) is missing on the record.
  getOptionTrailing?: (item: T) => ReactNode;
  // Optional per-row disable — disabled rows can't be selected.
  getOptionDisabled?: (item: T) => boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  size?: Size;
  className?: string;
  style?: CSSProperties;
  id?: string;
  leadingIcon?: ReactNode;
  debounceMs?: number;
  // Render the dropdown in a portal with fixed positioning so it escapes
  // `overflow: hidden` ancestors (e.g. the operations card). Off by default.
  usePortal?: boolean;
  // Minimum width for the portaled menu. Without it a `select`-appearance menu
  // is locked to the trigger's exact width, which truncates option text when the
  // trigger sits in a narrow column. Setting this frees the menu to grow.
  dropdownMinWidth?: CSSProperties['minWidth'];
  // Trigger chrome: `select` (default — bordered field with a chevron) or
  // `button` (compact ghost button: icon + label, no border/chevron) for
  // inline "Add X" affordances.
  appearance?: 'select' | 'button';
};

export const ServerSearchSelect = <T,>({
  value,
  selectedLabel,
  onChange,
  useSearch,
  getOptionValue,
  getOptionLabel,
  getOptionContent,
  getOptionTrailing,
  getOptionDisabled,
  placeholder,
  searchPlaceholder = 'Search…',
  emptyMessage = 'No matches',
  disabled,
  size = 'default',
  className,
  style,
  id,
  leadingIcon,
  debounceMs = 200,
  usePortal = false,
  dropdownMinWidth,
  appearance = 'select',
}: ServerSearchSelectProps<T>) => {
  const isButton = appearance === 'button';
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, debounceMs);
  // Pass the open flag through so consumers can disable their request while the
  // panel is closed — saves a round-trip per embedded picker on first render.
  const {
    data: results,
    isLoading,
    isError,
  } = useSearch(open ? debouncedQuery : '', open);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [portalPosition, setPortalPosition] = useState<PortalPosition | null>(
    null
  );

  // Portal: compute trigger-relative fixed position; recompute on scroll/resize.
  useLayoutEffect(() => {
    if (!open || !usePortal) {
      setPortalPosition(null);
      return undefined;
    }
    const computePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placement: PortalPosition['placement'] =
        spaceBelow < PORTAL_DROPDOWN_MAX_HEIGHT + PORTAL_DROPDOWN_GAP &&
        spaceAbove > spaceBelow
          ? 'top'
          : 'bottom';
      // A menu freed by `dropdownMinWidth` grows rightward past the trigger, so
      // the overflow estimate has to use that width — not the trigger's — or a
      // picker in a right-hand column renders half off-screen.
      const estWidth = Math.max(
        rect.width,
        typeof dropdownMinWidth === 'number' ? dropdownMinWidth : 0
      );
      setPortalPosition({
        top:
          placement === 'top'
            ? rect.top - PORTAL_DROPDOWN_GAP
            : rect.bottom + PORTAL_DROPDOWN_GAP,
        left: rect.left,
        right: window.innerWidth - rect.right,
        width: rect.width,
        placement,
        alignRight:
          rect.left + estWidth >
          window.innerWidth - PORTAL_DROPDOWN_EDGE_MARGIN,
      });
    };
    computePosition();
    const onScroll = () => computePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, usePortal, dropdownMinWidth]);

  // Reset + focus the search input on open; clear query on close.
  useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  // Outside click closes.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideRoot = rootRef.current?.contains(target);
      const insidePortal = portalRef.current?.contains(target);
      if (!insideRoot && !insidePortal) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleSelect = (item: T) => {
    onChange(getOptionValue(item));
    setOpen(false);
  };

  const hasValue = value != null && value !== '';
  const display = hasValue
    ? (selectedLabel ?? placeholder ?? ' ')
    : (placeholder ?? ' ');

  const dropdownContent = (
    <>
      <div className={css.searchRow}>
        <Search className={css.searchIcon} />
        <input
          aria-label={searchPlaceholder}
          className={css.searchInput}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchPlaceholder}
          ref={inputRef}
          type="text"
          value={query}
        />
        {isLoading && <Loader2 className={css.spinner} />}
      </div>
      <div className={css.optionList}>
        {(results ?? []).map((item) => {
          const optionValue = getOptionValue(item);
          const isSelected = optionValue === value;
          const trailing = getOptionTrailing?.(item);
          const optionDisabled = getOptionDisabled?.(item) ?? false;
          return (
            <div className={css.optionRow} key={optionValue}>
              <button
                className={joinCss(
                  css.option,
                  isSelected && css.optionSelected
                )}
                disabled={optionDisabled}
                onClick={() => handleSelect(item)}
                type="button"
              >
                <span className={css.checkSlot}>
                  {isSelected && <Check className={css.checkIcon} />}
                </span>
                {getOptionContent ? (
                  <span className={css.optionContent}>
                    {getOptionContent(item)}
                  </span>
                ) : (
                  <span className={css.optionLabel}>
                    {getOptionLabel(item)}
                  </span>
                )}
              </button>
              {/* Trailing actions live beside the option button (not nested)
                  so they can be real interactive controls. */}
              {trailing && (
                <span className={css.optionTrailing}>{trailing}</span>
              )}
            </div>
          );
        })}
        {!isLoading && (results?.length ?? 0) === 0 && (
          <span className={css.empty}>
            {isError ? 'Search failed — please try again' : emptyMessage}
          </span>
        )}
      </div>
    </>
  );

  const renderPortalDropdown = () => {
    if (typeof document === 'undefined' || !portalPosition) return null;
    const portalStyle: CSSProperties = {
      ...(portalPosition.alignRight
        ? { right: portalPosition.right }
        : { left: portalPosition.left }),
      // A `button` trigger is content-sized (often narrow), so don't lock the
      // menu to it. A `select` trigger normally matches its field width — but a
      // caller in a narrow column can pass `dropdownMinWidth` to let the menu
      // grow past the trigger instead of squeezing option text.
      ...(isButton
        ? { minWidth: BUTTON_MENU_MIN_WIDTH }
        : dropdownMinWidth !== undefined
          ? { minWidth: dropdownMinWidth }
          : { minWidth: portalPosition.width, width: portalPosition.width }),
      ...(portalPosition.placement === 'top'
        ? { bottom: window.innerHeight - portalPosition.top }
        : { top: portalPosition.top }),
    };
    return createPortal(
      <div
        className={joinCss(css.dropdown, css.dropdownPortal)}
        ref={portalRef}
        style={portalStyle}
      >
        {dropdownContent}
      </div>,
      document.body
    );
  };

  return (
    <div
      className={joinCss(css.wrapper, className)}
      ref={rootRef}
      style={style}
    >
      <button
        className={styles({
          size,
          isDisabled: !!disabled,
          isOpen: open,
          appearance: isButton ? 'button' : undefined,
        })}
        disabled={disabled}
        id={id}
        onClick={() => setOpen((prev) => !prev)}
        ref={triggerRef}
        type="button"
      >
        {leadingIcon && <span className={css.icon}>{leadingIcon}</span>}
        <span className={hasValue ? css.value : css.placeholder}>
          {display}
        </span>
        {!isButton && (
          <ChevronDown
            className={joinCss(css.chevron, open && css.chevronOpen)}
          />
        )}
      </button>

      {open &&
        (usePortal ? (
          renderPortalDropdown()
        ) : (
          <div className={css.dropdown}>{dropdownContent}</div>
        ))}
    </div>
  );
};
